import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/** How long the pointer must rest on the line before its people show (as the catalog's item info). */
export const CREW_INFO_DELAY_MS = 300;

/** The event's „Obsada” tab (owner: „link tekstowy”, plain underlined text). */
export const crewTabUrl = (eventId: string): string => `/wydarzenia?wydarzenie=${eventId}&zakladka=obsada`;

/**
 * Owner 2026-10-25 01:56 / 01:58 („dymek na usłudze pokazuje tylko osobę podpiętą i link do obsady”): the names of the
 * people behind a crew line, one per line, and the text link „Obsada” — only the link when nobody is on the line.
 * Shown while the pointer rests on the line's name cell (`hovered`) or the keyboard focus is on „z obsady”; on a phone a tap on „z obsady” opens and closes it. Only what the server sent (rule 7a).
 */
export function CrewPeopleInfo({ people, eventId, hovered, label }: { people: readonly string[]; eventId?: string | undefined; hovered: boolean; label: string }) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clear = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(() => {
    clear();
    if (hovered) timer.current = setTimeout(() => setOpen(true), CREW_INFO_DELAY_MS);
    else setOpen(false);
    return clear;
  }, [hovered]);
  const shown = open || pinned;
  return (
    <span
      className="q-crew-info"
      onFocus={() => setOpen(true)}
      onBlur={(e) => {
        if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) {
          setOpen(false);
          setPinned(false);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && shown) {
          e.stopPropagation();
          setOpen(false);
          setPinned(false);
        }
      }}
    >
      <button type="button" className="q-crew-btn" aria-expanded={shown} aria-describedby={id} aria-label={`Kto jest w obsadzie: ${label}`} onClick={() => setPinned(!pinned)}>
        z obsady
      </button>
      <span id={id} role="tooltip" className="q-crew-pop" hidden={!shown}>
        {people.map((name, i) => (
          <span key={i} className="q-crew-person">
            {name}
          </span>
        ))}
        {eventId && (
          <Link className="q-crew-link" to={crewTabUrl(eventId)}>
            Obsada
          </Link>
        )}
      </span>
    </span>
  );
}
